import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gamificationAPI, resolveFileUrl } from '../services/api';

const PURPLE = '#200854';
const ORANGE = '#FF6600';

const LEVEL_COLORS: Record<string, string> = {
  Rider: '#9CA3AF',
  Grinder: '#F59E0B',
  Pacer: '#3B82F6',
  Dominator: '#EF4444',
};

interface PublicProfile {
  id: string;
  name: string | null;
  surname: string | null;
  photoUrl: string | null;
  riderLevel: string | null;
  averagePace: string | null;
  bikeType: string | null;
  bike: string | null;
  totalPoints: number;
  rideCount: number;
  currentStreak: number;
  level: {
    level: number;
    name: string;
    minPoints: number;
    maxPoints: number;
    nextLevel: { level: number; name: string; pointsNeeded: number } | null;
  };
  memberSince: string;
}

const UserProfile: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!userId) return;
    gamificationAPI.getUserProfile(userId)
      .then(setProfile)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [userId]);

  const displayName = profile?.name && profile?.surname
    ? `${profile.name} ${profile.surname}`
    : t('najsport.rider');

  const initials = profile
    ? ((profile.name?.[0] || '') + (profile.surname?.[0] || '')).toUpperCase() || '?'
    : '?';

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="shadow-md" style={{ backgroundColor: PURPLE }}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/najsport-logo.png" alt="NajSport" className="h-8 w-auto" />
            <h1 className="text-lg font-bold text-white">{t('najsport.profileView')}</h1>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-sm text-white hover:text-orange-300 transition-colors font-medium"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            {t('najsport.back')}
          </button>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-8">
        {loading && (
          <div className="bg-white rounded-2xl shadow-lg p-6 space-y-5 animate-pulse">
            <div className="flex flex-col items-center gap-3">
              <div className="w-28 h-28 rounded-full bg-gray-200" />
              <div className="h-4 bg-gray-200 rounded w-32" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[1, 2, 3].map(i => <div key={i} className="bg-gray-100 rounded-xl p-3 h-16" />)}
            </div>
          </div>
        )}

        {error && (
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center text-gray-500">
            {t('najsport.userNotFound')}
          </div>
        )}

        {profile && !loading && (
          <div className="bg-white rounded-2xl shadow-lg p-6 space-y-5">
            {/* Avatar & Name */}
            <div className="flex flex-col items-center gap-3">
              {profile.photoUrl ? (
                <img
                  src={resolveFileUrl(profile.photoUrl)}
                  alt={displayName}
                  className="w-28 h-28 rounded-full object-cover border-4"
                  style={{ borderColor: LEVEL_COLORS[profile.level.name] || PURPLE }}
                />
              ) : (
                <div
                  className="w-28 h-28 rounded-full flex items-center justify-center text-white text-3xl font-bold border-4"
                  style={{
                    backgroundColor: PURPLE,
                    borderColor: LEVEL_COLORS[profile.level.name] || PURPLE,
                  }}
                >
                  {initials}
                </div>
              )}
              <div className="text-center">
                <div className="text-xl font-bold" style={{ color: PURPLE }}>{displayName}</div>
                <div
                  className="inline-block mt-1 px-3 py-0.5 rounded-full text-white text-xs font-bold"
                  style={{ backgroundColor: LEVEL_COLORS[profile.level.name] || PURPLE }}
                >
                  Lv.{profile.level.level} {profile.level.name}
                </div>
              </div>
            </div>

            {/* Level Progress */}
            <div className="rounded-xl p-4" style={{ background: `linear-gradient(135deg, ${PURPLE}, #4a1d96)` }}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-orange-400 text-xs font-bold uppercase tracking-wider">Level {profile.level.level}</div>
                <div className="text-2xl font-bold text-white">{profile.totalPoints} <span className="text-sm text-purple-200">{t('najsport.points').toLowerCase()}</span></div>
              </div>
              {profile.level.nextLevel && (
                <div>
                  <div className="flex justify-between text-xs text-purple-200 mb-1">
                    <span>{profile.level.name}</span>
                    <span>{profile.level.nextLevel.name} ({profile.level.nextLevel.pointsNeeded})</span>
                  </div>
                  <div className="w-full bg-purple-900/50 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all"
                      style={{
                        backgroundColor: ORANGE,
                        width: `${Math.min(100, ((profile.totalPoints - profile.level.minPoints) / (profile.level.nextLevel.pointsNeeded - profile.level.minPoints)) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-purple-50 rounded-xl p-3">
                <div className="text-2xl font-bold" style={{ color: PURPLE }}>{profile.totalPoints}</div>
                <div className="text-[11px] text-gray-500 mt-0.5">{t('najsport.points')}</div>
              </div>
              <div className="bg-purple-50 rounded-xl p-3">
                <div className="text-2xl font-bold" style={{ color: PURPLE }}>{profile.rideCount}</div>
                <div className="text-[11px] text-gray-500 mt-0.5">{t('najsport.ridesCount')}</div>
              </div>
              <div className="bg-purple-50 rounded-xl p-3">
                <div className="text-2xl font-bold" style={{ color: PURPLE }}>{profile.currentStreak}</div>
                <div className="text-[11px] text-gray-500 mt-0.5">{t('najsport.streak')}</div>
              </div>
            </div>

            {/* Member Since */}
            <div className="text-center text-xs text-gray-400 pt-2 border-t">
              {t('najsport.memberSince')} {new Date(profile.memberSince).toLocaleDateString('sk-SK', { month: 'long', year: 'numeric' })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default UserProfile;
